(() => {
  if (customElements.get('muck-sprite')) return;
  class MuckSprite extends HTMLElement {
    async connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas?.getContext('2d');
      if (!this.context) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.paused = false;
      this.rest = this.dataset.rest || 'wistful';
      this.querySelector('[data-muck-pause]')?.addEventListener('click', (event) => {
        this.paused = !this.paused;
        event.currentTarget.setAttribute('aria-pressed', String(this.paused));
        this.resume();
      }, { signal });
      if (this.dataset.kind === 'cart') {
        this.onCart = () => {
          if (!this.manifest || !this.atlas?.complete) { this.pendingCart = true; return; }
          if (this.cartActive) return;
          this.cartActive = true;
          this.play('add-to-cart', true);
          clearTimeout(this.cartTimer);
          this.cartTimer = setTimeout(() => {
            this.cartActive = false;
            this.play('still', true);
          }, 1800);
        };
        this.closest('cart-notification, cart-drawer')?.addEventListener('muck:summary-added', this.onCart, { signal });
      }
      this.motion.addEventListener('change', () => this.resume(), { signal });
      document.addEventListener('visibilitychange', () => this.resume(), { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.loadWhenVisible?.();
        this.resume();
      });
      this.observer.observe(this);
      try {
        if (['counting', 'bagging', 'story'].includes(this.dataset.kind) && !this.visible) {
          await new Promise((resolve, reject) => {
            this.loadWhenVisible = resolve;
            signal.addEventListener('abort', () => reject(new Error('Sprite disconnected')), { once: true });
          });
          this.loadWhenVisible = null;
        }
        const response = await fetch(this.dataset.manifest, { signal });
        if (!response.ok) throw new Error('Sprite unavailable');
        this.manifest = await response.json();
        if (this.dataset.kind === 'story') {
          this.manifest.animations = { story: { loop: true, poster: this.manifest.timeline[0].id, frames: this.manifest.timeline } };
        }
        this.atlas = new Image();
        this.atlas.src = this.dataset.atlas;
        await this.atlas.decode();
        if (signal.aborted) return;
        const builder = this.closest('hoard-builder');
        if (builder?.getState) {
          const state = builder.getState();
          this.setHoard(builder.hoardScore ? builder.hoardScore() : Object.values(state.categories).reduce((sum, c) => sum + (c.enabled
            ? c.lines.reduce((n, l) => n + l.quantity * ({small:1,medium:3,large:6}[l.size] || 1), 0) : 0), 0));
        }
        this.play(this.rest, true);
        if (this.pendingCart) { this.pendingCart = false; this.onCart(); }
      } catch (error) {
        if (!signal.aborted) this.hidden = true;
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.cartTimer);
    }
    setHoard(score) {
      const tier = score === 0 ? 0 : score < 3 ? 1 : score < 9 ? 2 : 3;
      const previous = this.tier;
      const previousScore = this.score;
      this.score = score;
      this.tier = tier;
      this.rest = ['wistful', 'curious', 'pleased', 'smug'][tier];
      if (previous === tier) {
        if (previousScore !== undefined && score > previousScore) this.play('count');
        return;
      }
      this.play(previous !== undefined && tier > previous ? 'celebrate' : this.rest, true);
    }
    play(name, replace = false) {
      if (!this.manifest || !this.atlas?.complete) return;
      if (!replace && this.animation && !this.animation.loop && this.animation.motion !== false) return;
      this.animation = this.manifest.animations[name] || this.manifest.animations[this.rest];
      this.index = 0;
      this.resume();
    }
    resume() {
      clearTimeout(this.timer);
      if (!this.animation) return;
      if (this.paused) return;
      const concealed = this.dataset.kind === 'hiding' &&
        (this.motion.matches || this.animation === this.manifest.animations[this.rest]);
      this.classList?.toggle('is-concealed', concealed);
      if (this.motion.matches) {
        this.draw(concealed ? null : this.animation.poster);
        return;
      }
      if (this.animation === this.manifest.animations[this.rest] && !['cart', 'counting', 'bagging', 'story'].includes(this.dataset.kind)) {
        this.draw(concealed ? null : this.animation.poster);
        if (!this.visible || document.hidden) return;
        this.timer = setTimeout(() => {
          this.ambient = true;
          const action = this.dataset.kind === 'lost' && matchMedia('(max-width: 749px)').matches
            ? 'search' : this.dataset.action || 'count';
          this.play(action, true);
        }, 12000 + Math.random() * 12000);
        return;
      }
      if (this.animation.motion === false) {
        this.draw(this.animation.poster);
        return;
      }
      this.draw(this.animation.frames[this.index].id, this.animation.frames[this.index].offset_y || 0);
      if (!this.visible || document.hidden) return;
      this.timer = setTimeout(() => {
        this.index += 1;
        if (this.index >= this.animation.frames.length) {
          if (!this.animation.loop || this.ambient) {
            this.ambient = false;
            return this.play(this.rest, true);
          }
          this.index = 0;
        }
        this.resume();
      }, this.animation.frames[this.index].duration_ms);
    }
    draw(id, offset = 0) {
      if (this.dataset.kind === 'story') {
        this.canvas.width = Math.max(240, Math.round(this.getBoundingClientRect().width * 3));
        this.canvas.height = 240;
      }
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      const frame = this.manifest.frames[id];
      if (!frame) return;
      this.context.imageSmoothingEnabled = false;
      this.context.save();
      if (this.dataset.kind === 'hiding') {
        this.context.beginPath();
        this.context.rect(0, 0, 192, 132);
        this.context.clip();
      }
      if (frame.layers) {
        for (const layer of frame.layers) this.context.drawImage(this.atlas, ...layer.rect, ...layer.at);
      } else {
        this.context.drawImage(this.atlas, ...frame.rect, 0, offset, 192, 192);
      }
      this.context.restore();
    }
  }
  customElements.define('muck-sprite', MuckSprite);
})();
